import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from as observableFrom, Observable, throwError as observableThrowError } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { serviceWorkerFetchStrategyHeader } from '../../service-worker/service-worker-fetch-strategy-header.constant';
import { FetchStrategy } from '../../shared/instrumentation/instrumented-http.service';
import { HttpResponseContentType } from '../../shared/models/http-response-content-type.enum';
import { DiskPreloadService } from './disk-preload.service';

/**
 * This `HttpInterceptor` is designed to be used in conjunction with a lightweight inline script, placed before the main
 * 'app' bundle parse. The inline script is responsible for reading from SW Disk Cache ASAP _before_ the bundle is parsed, and
 * placing the results into the `diskPreloadRequestMap`.
 * This interceptor will look for requests that are for `cache-only`, and instead of requiring a 'round-trip' + Disk Read to Service Worker,
 * it will use the pre-loaded response in the Map.
 * This will allow critical resources to be loaded up into memory while the app-bundle is parsing.
 */
@Injectable()
export class DiskPreloadInterceptor implements HttpInterceptor {
    constructor(
        private readonly diskPreloadService: DiskPreloadService
    ) {
    }

    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (request.headers.get(serviceWorkerFetchStrategyHeader) === FetchStrategy.CacheOnly) {
            return observableFrom(this.diskPreloadService.loadPreloadResponse(request.urlWithParams)).pipe(
                switchMap(preloadedFetchResponse => {
                    if (!preloadedFetchResponse) {
                        return next.handle(request);
                    }

                    if (!preloadedFetchResponse.ok) {
                        return observableThrowError(this.convertToAngularHttpErrorResponse(preloadedFetchResponse));
                    }

                    return observableFrom(
                        this.convertToAngularHttpResponse(preloadedFetchResponse, request.responseType as HttpResponseContentType)
                    );
                })
            );

        }

        return next.handle(request).pipe(
            tap(httpEvent => {
                if (httpEvent instanceof HttpResponse && httpEvent.ok) {
                    this.diskPreloadService.tryAddToPreloadList(request.urlWithParams);
                }
            })
        );
    }

    private async convertToAngularHttpResponse(from: Response, responseType: HttpResponseContentType): Promise<HttpResponse<any>> {
        let body: any;

        switch (responseType) {
            case HttpResponseContentType.Json:
                body = await from.json();
                break;
            case HttpResponseContentType.Text:
                body = await from.text();
                break;
            case HttpResponseContentType.ArrayBuffer:
                body = await from.arrayBuffer();
                break;
            case HttpResponseContentType.Blob:
                body = await from.blob();
                break;
        }

        return new HttpResponse<any>({
            body,
            headers: this.convertToAngularHeaders(from.headers),
            status: from.status,
            statusText: from.statusText,
            url: from.url
        });
    }

    private convertToAngularHttpErrorResponse(from: Response) {
        return new HttpErrorResponse({
            error: from.statusText,
            status: from.status,
            statusText: from.statusText,
            headers: this.convertToAngularHeaders(from.headers),
            url: from.url
        });
    }

    private convertToAngularHeaders(headers: Headers): HttpHeaders {
        let httpHeaders = new HttpHeaders();

        headers.forEach((headerValue, headerName) => {
            httpHeaders = httpHeaders.append(headerName, headerValue);
        });

        return httpHeaders;
    }
}
